<script setup>
import { computed, ref, onMounted, onUnmounted } from 'vue'
import GeneralEmbedButton from '@/Components/Links/GeneralEmbedButton.vue'
import GeneralButton from '@/Components/Links/GeneralButton.vue'
import AppButton from '@/Components/AppButton.vue'
import CardTitle from '@/Components/Links/CardTitle.vue'
import BootstrapModal from '@/Components/BootstrapModal.vue'
import { buildLinkAssetUrl } from '@/utils/assetUrls.js'

const props = defineProps({
  link: { type: Object, required: true },
  appearance: { type: Object, default: () => ({}) },
  buttonStyle: { type: [Object, Array, String], default: () => ({}) },
  DoBucket: String,
  DoEndpoint: String,
})

const emit = defineEmits(['click', 'share'])

// Layout: classic | featured
const layout = computed(() => props.link?.options?.layout || 'classic')

// Modal handling (similar to GoogleMapCard)
const modalRef = ref(null)
const modalId = computed(() => {
  const idPart = props.link?.id ?? (props.link?.url ? props.link.url.replace(/[^a-zA-Z0-9]/g, '').slice(0, 24) : 'default')
  return `typeform-modal-${idPart}`
})
function openFullscreenModal(e){
  e?.preventDefault?.()
  e?.stopPropagation?.()
  // Route through parent handler so it can check locks and open unlock modal if needed
    emit('click', { link: props.link, mode: displayMode.value })
}

// Normalize display mode based on requirement:
// Read from link.options.typeform.display_mode (fallback link.form_option)
// Supported: 'embed-typeform', 'link-to-typeform' (alias 'go-directly'), 'show_open_button'
function normalizeMode(raw){
  const s = String(raw || '').toLowerCase().trim()
  if (!s) return ''
  if (s === 'go-directly') return 'link-to-typeform'
  return s
}

const displayMode = computed(() => {
  const tf = props.link?.options?.typeform || {}
  // Preferred new field
  let m = normalizeMode(tf.display_mode || props.link?.form_option)
  if (m) return m
  // Legacy booleans fallback from existing options
  if (tf.embed === true) return 'embed-typeform'
  if (tf.link === true) return 'link-to-typeform'
  return 'link-to-typeform' // default
})

const isEmbed = computed(() => displayMode.value === 'embed-typeform')
const isLinkOnly = computed(() => displayMode.value === 'link-to-typeform')
const showOpenButton = computed(() => {
  const tf = props.link?.options?.typeform || {}
  if (displayMode.value === 'show_open_button') return true
  return tf.show_open_button === true
})

// Detect mobile viewport to switch to compact preview with a Start button
const isMobile = ref(typeof window !== 'undefined' ? window.innerWidth <= 768 : false)
function handleResize(){
  if (typeof window !== 'undefined') {
    isMobile.value = window.innerWidth <= 768
  }
}
onMounted(() => {
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', handleResize)
  }
})
onUnmounted(() => {
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', handleResize)
  }
})

function onRootClick(e){
  e?.preventDefault?.()
  e?.stopPropagation?.()
    emit('click', { link: props.link, mode: displayMode.value })
}
function onShare(e){ e?.stopPropagation?.(); emit('share', props.link) }
function openInNewTab(e){
  e?.stopPropagation?.()
  // Route through parent lock flow
    emit('click', { link: props.link, mode: displayMode.value })
}

const thumbUrl = computed(() => {
  const file = props.link?.file
  if (file) return buildLinkAssetUrl(props.DoEndpoint, props.DoBucket, file)
  return ''
})


// Detect when the card/button style requests no border radius so the internal buttons match
const hasNoRadius = computed(() => {
  function containsRounded0(val){
    if (!val) return false;
    if (typeof val === 'string') return val.includes('rounded-0');
    if (Array.isArray(val)) return val.some(v => typeof v === 'string' && v.includes('rounded-0'));
    if (typeof val === 'object') return !!val['rounded-0'];
    return false;
  }
  return containsRounded0(props.appearance?.buttons) || containsRounded0(props.buttonStyle);
})
</script>

<template>
  <!-- FEATURED LAYOUT -->
  <GeneralEmbedButton
    v-if="layout==='featured'"
    class="typeform-card"
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onRootClick"
  >
    <!-- Header: left icon, centered title, right share -->
    <div class="d-flex align-items-center mb-2" style="height:3rem; padding-top:6px; padding-left:12px; padding-right:12px; gap:8px;">
      <!-- Left Typeform Icon -->
      <div class="d-flex align-items-center">
        <svg width="1.5rem" height="1.5rem" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" :style="{ color: appearance.button_font_color || '#000' }" aria-label="Typeform" role="img">
          <rect x="2" y="2" width="20" height="20" rx="6" ry="6" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <path d="M7 8.5h10M12 8.5v9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
      <!-- Center Title between icons -->
      <CardTitle :title="link.title" layout="classic" align="center" :max="100" class="flex-grow-1" style="min-width:0" />
      <!-- Share icon on the right -->
      <span class="d-flex align-items-center" aria-label="Share" style="flex-shrink:0">
        <svg @click.prevent.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
                d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
        </svg>
      </span>
    </div>

    <!-- Inline embed for featured layout when in 'embed-typeform' mode -->
    <div v-if="isEmbed && link?.url" class="mb-3" @click.stop>
      <!-- Desktop/tablet: show inline embed -->
      <div v-if="!isMobile" class="w-100 tf-embed" style="height: 420px; border-radius: 12px; overflow: hidden;">
        <iframe :src="link.url" title="Typeform" allow="fullscreen; geolocation; microphone; camera" style="width:100%; height:100%; border:0; display:block;"></iframe>
      </div>
      <!-- Mobile: compact preview with Start form button that opens fullscreen modal -->
      <div v-else class="w-100" style="border-radius: 12px; overflow: hidden;">
        <div class="p-3 d-flex flex-column align-items-center text-center" style="gap: 12px;">
            <AppButton width="100%" :secondary="true" @click.stop="openFullscreenModal"  :fontWeight="appearance.font_weight"  :custom-class="[appearance.buttons]" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color" ignore-plan-restriction>Start form</AppButton>

        </div>
      </div>
    </div>

    <!-- Open in new tab area (optional) -->
    <div v-if="showOpenButton" class="text-center">
        <AppButton width="100%" :secondary="true" @click.stop="openInNewTab" :fontWeight="appearance.font_weight"   :custom-class="[appearance.buttons]" :backgroundColor="appearance.button_color" :textColor="appearance.button_font_color" ignore-plan-restriction>Start form</AppButton>
    </div>
  </GeneralEmbedButton>

  <!-- CLASSIC LAYOUT -->
  <GeneralButton
    v-else
    :link="link"
    :appearance="appearance"
    :buttonStyle="buttonStyle"
    :DoBucket="DoBucket"
    :DoEndpoint="DoEndpoint"
    :layout="layout"
    @click="onRootClick"
  >
    <template #left>
      <div v-if="!thumbUrl" class="d-flex align-items-center justify-content-center" style="width:40px; height:40px; border-radius:6px;">
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" :style="{ color: appearance.button_font_color || '#000' }" aria-label="Typeform" role="img">
          <rect x="2" y="2" width="20" height="20" rx="6" ry="6" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <path d="M7 8.5h10M12 8.5v9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
        </svg>
      </div>
    </template>
    <template #title>
      <CardTitle :title="link.title" layout="classic" align="center" :max="100" />
    </template>
    <template #actions>
      <svg @click.stop="onShare" width="1.5rem" height="1.5rem" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path fill-rule="evenodd" clip-rule="evenodd" :fill="appearance.button_font_color || '#000'"
              d="M16.5 2.25C14.7051 2.25 13.25 3.70507 13.25 5.5C13.25 5.69591 13.2673 5.88776 13.3006 6.07412L8.56991 9.38558C8.54587 9.4024 8.52312 9.42038 8.50168 9.43939C7.94993 9.00747 7.25503 8.75 6.5 8.75C4.70507 8.75 3.25 10.2051 3.25 12C3.25 13.7949 4.70507 15.25 6.5 15.25C7.25503 15.25 7.94993 14.9925 8.50168 14.5606C8.52312 14.5796 8.54587 14.5976 8.56991 14.6144L13.3006 17.9259C13.2673 18.1122 13.25 18.3041 13.25 18.5C13.25 20.2949 14.7051 21.75 16.5 21.75C18.2949 21.75 19.75 20.2949 19.75 18.5C19.75 16.7051 18.2949 15.25 16.5 15.25C15.4472 15.25 14.5113 15.7506 13.9174 16.5267L9.43806 13.3911C9.63809 12.9694 9.75 12.4978 9.75 12C9.75 11.5022 9.63809 11.0306 9.43806 10.6089L13.9174 7.4733C14.5113 8.24942 15.4472 8.75 16.5 8.75C18.2949 8.75 19.75 7.29493 19.75 5.5C19.75 3.70507 18.2949 2.25 16.5 2.25ZM14.75 5.5C14.75 4.5335 15.5335 3.75 16.5 3.75C17.4665 3.75 18.25 4.5335 18.25 5.5C18.25 6.4665 17.4665 7.25 16.5 7.25C15.5335 7.25 14.75 6.4665 14.75 5.5ZM6.5 10.25C5.5335 10.25 4.75 11.0335 4.75 12C4.75 12.9665 5.5335 13.75 6.5 13.75C7.4665 13.75 8.25 12.9665 8.25 12C8.25 11.0335 7.4665 10.25 6.5 10.25ZM16.5 16.75C15.5335 16.75 14.75 17.5335 14.75 18.5C14.75 19.4665 15.5335 20.25 16.5 20.25C17.4665 20.25 18.25 19.4665 18.25 18.5C18.25 17.5335 17.4665 16.75 16.5 16.75Z"/>
      </svg>
    </template>
  </GeneralButton>

  <!-- Fullscreen Bootstrap modal for Typeform -->
  <teleport to="body">
    <BootstrapModal
      ref="modalRef"
      :modal-id="modalId"
      :title="link.title || 'Typeform'"
      modalSize="fullscreen"
    >
      <template #body>
        <div v-if="link?.url" class="w-100" style="height: calc(100vh - 140px); position: relative; overflow:hidden;">
          <div class="w-100 h-100 tf-embed">
            <iframe :src="link.url" title="Typeform" allow="fullscreen; geolocation; microphone; camera" style="width:100%; height:100%; border:0; display:block;"></iframe>
          </div>
        </div>
        <div v-else class="p-3">
          <AppButton ignore-plan-restriction width="100%" :secondary="true" @click.stop="openInNewTab" :custom-class="appearance.buttons">Open in new tab</AppButton>
        </div>
      </template>
    </BootstrapModal>
  </teleport>
</template>

<style scoped>
/* Ensure embedded iframe inside the fullscreen modal fills the available space */
.tf-embed :deep(iframe){
  width: 100% !important;
  height: 100% !important;
  border: 0 !important;
  display: block;
}
</style>
